import { ERequestSubmitted, IRequest } from 'legacy/lut/classes/models/onboarding/IRequest'
import { REQUEST_DETAIL_PARTNER_FORM } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestDetail/actionView/dataCollector/FormComponents/requestDetailPartnerFormWrapper/translations'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { PartnerHierarchyType, PartnerStatus } from 'clickNgo/entities/partner/configs'
import { IPartner, IPartnerDuplicate } from 'clickNgo/entities/partner/models'
import { InfoBlockType } from 'cng-common/components/infoBlock'
import { Translation } from 'cng-common/features/translations'
import logger from 'core/utils/Logger'
import { InvalidChangePartnerError } from 'onboarding/classes/errors/InvalidChangePartnerError'
import { ValidationParamsError } from 'onboarding/classes/errors/ValidationParamsError'
import { PartnerHelper } from 'onboarding/classes/helpers/PartnerHelper'
import { showError } from 'onboarding/classes/services/notification'
import { searchOnboardingPartnerDuplicates } from 'onboarding/helpers/searchOnboardingPartnerDuplicates'
import { requestService } from 'onboarding/services/requestService'
import { showErrorOnCatch } from 'onboarding/utils/errorResponse/showErrorOnCatch'
import { tryCatchAsync } from 'onboarding/utils/tryCatch'

interface IUseApolloData {
  partner: IPartner
  request: IRequest
  changeLoader: (flag: boolean) => void
  onReplacePartner: ({ partner }: { partner: IPartner }, callback?: () => void) => void
  onActivatePartner: ({ partner }: { partner: IPartner }, callback?: () => void) => void
}

export const useApolloData = ({
  changeLoader,
  partner,
  request,
  onReplacePartner,
  onActivatePartner,
}: IUseApolloData) => {
  const [apolloResult, setApolloResult] = useState<IPartnerDuplicate[]>([])
  const [invalidChangePartnerReason, setInvalidChangePartnerReason] = useState<string | null>(null)
  const [isPartnerChangeable, setIsPartnerChangeable] = useState(false)
  const [isApolloResultVisible, setIsApolloResultVisible] = useState(false)

  const hasApolloResultCriticalMatch = useMemo(
    () => apolloResult.some((el) => el.hasCriticalMatch),
    [apolloResult]
  )

  const onPartnerSearchInApollo = useCallback(async () => {
    try {
      changeLoader(true)
      if (partner) {
        const duplicates = await searchOnboardingPartnerDuplicates({ partner })
        setApolloResult(duplicates)
        setIsApolloResultVisible(duplicates.length > 0)
        if (!duplicates.length) {
          showError('Apollo has no duplicate partners!')
        }
      }
    } catch (e: any) {
      setApolloResult([])
      showErrorOnCatch(e)
    } finally {
      changeLoader(false)
    }
  }, [changeLoader, partner])

  const resetApolloResult = useCallback(() => {
    setIsApolloResultVisible(false)
    setApolloResult([])
  }, [])

  const partnerDuplicatesHeaderActions = useMemo(
    () => [
      {
        title: 'Close',
        onClick: resetApolloResult,
        checkVisibility: () => true,
      },
    ],
    [resetApolloResult]
  )

  const update = useCallback(
    (partnerId: string, data: Partial<IPartner>) => {
      const result = apolloResult.find((i) => i.partner.partnerId === partnerId)
      if (result) {
        result.isInactive = data.status !== PartnerStatus.Active
        result.partner = { ...result.partner, ...data }
        setApolloResult([...apolloResult])
      }
    },
    [apolloResult]
  )

  const partnerDuplicatesContentActions = [
    {
      title: 'Replace partner',
      onClick: onReplacePartner,
      checkVisibility: ({ duplicate }: { duplicate: IPartnerDuplicate }) => {
        const {
          isInactive,
          partner: { status },
        } = duplicate
        const isPortalOnlineRequest =
          request.Region === 'AMS' &&
          (request.Submitted == ERequestSubmitted.online ||
            request.Submitted == ERequestSubmitted.rcs)

        const isActivateWIP = [PartnerStatus.Compliance, PartnerStatus.Activating].includes(
          status as PartnerStatus
        )

        return (
          isPartnerChangeable &&
          duplicate.partner.hierarchyType === PartnerHierarchyType.HEADQUARTERS &&
          Boolean(!isInactive) &&
          !isActivateWIP &&
          !isPortalOnlineRequest
        )
      },
    },
    {
      title: 'Activate',
      onClick: onActivatePartner,
      checkVisibility: ({ duplicate }: { duplicate: IPartnerDuplicate }) => {
        const {
          isInactive,
          partner: { status },
        } = duplicate
        return (
          Boolean(isInactive) &&
          ![PartnerStatus.Compliance, PartnerStatus.Activating].includes(status as PartnerStatus)
        )
      },
    },
  ]

  const partnerDuplicatesHeaderMessages = [
    {
      message: (
        <Translation text={REQUEST_DETAIL_PARTNER_FORM.apollo.criticalMatch} htmlAvailable />
      ),
      type: InfoBlockType.Danger,
      checkVisibility: () => {
        return (
          hasApolloResultCriticalMatch && PartnerHelper.isDummyPartnerRequest(partner.partnerId)
        )
      },
    },
    {
      message: REQUEST_DETAIL_PARTNER_FORM.apollo.noSuitablePartnerMessage,
      type: InfoBlockType.Danger,
      checkVisibility: () =>
        !hasApolloResultCriticalMatch &&
        PartnerHelper.isDummyPartnerRequest(partner.partnerId) &&
        apolloResult.length > 0,
    },
  ]

  const partnerDuplicatesContentMessages = [
    {
      message: 'The partner activation is under compliance review',
      checkVisibility: ({ duplicate }: { duplicate: IPartnerDuplicate }) => {
        const { status } = duplicate.partner
        return Boolean(status === PartnerStatus.Compliance)
      },
    },
    {
      message: `The partner has been successfully activated. To continue working with them, please wait
      for the system synchronization to complete (this usually takes 24 hours)`,
      checkVisibility: ({ duplicate }: { duplicate: IPartnerDuplicate }) => {
        const { status } = duplicate.partner
        return Boolean(status === PartnerStatus.Activating)
      },
    },
    {
      message: 'Inactive partner',
      checkVisibility: ({ duplicate }: { duplicate: IPartnerDuplicate }) => {
        const {
          isInactive,
          partner: { status },
        } = duplicate
        return (
          Boolean(isInactive) &&
          ![PartnerStatus.Compliance, PartnerStatus.Activating].includes(status as PartnerStatus)
        )
      },
    },
    {
      message: invalidChangePartnerReason || 'Partner is not changeable',
      checkVisibility: () => {
        return !isPartnerChangeable
      },
    },
    {
      message: (
        <div>
          <p>Click on 'Activate' button to trigger the partner activation API request to Apollo</p>
          <p>
            Note: If the inactive partner has Compliance fail flags, the tool will automatically
            trigger approval request to Market Compliance Lead. Partner will be activated only after
            Market Compliance Lead approves the removal of Compliance fail flag.
          </p>
          <p>
            Once this is completed, you will be able to replace the found activated partner via the
            'Replace partner' button.
          </p>
        </div>
      ),
      checkVisibility: ({ duplicate }: { duplicate: IPartnerDuplicate }) => {
        const {
          isInactive,
          partner: { status },
        } = duplicate
        return (
          Boolean(isInactive) &&
          ![PartnerStatus.Compliance, PartnerStatus.Activating].includes(status as PartnerStatus)
        )
      },
    },
  ]

  useEffect(() => {
    const canChangePartner = async () => {
      if (!request.EntryId) {
        return
      }

      changeLoader(true)
      let isChangeable = false
      const { error } = await tryCatchAsync<
        true,
        InvalidChangePartnerError | ValidationParamsError | Error
      >(requestService.canChangePartner(request.EntryId))

      if (error) {
        logger.error(error)
        if (error instanceof InvalidChangePartnerError) {
          setInvalidChangePartnerReason(error.message)
        } else {
          showError(error.message || 'Error')
        }
      } else {
        isChangeable = true
      }

      setIsPartnerChangeable(isChangeable)
      changeLoader(false)
    }

    canChangePartner()
  }, [request.EntryId, partner.partnerId, changeLoader])

  return {
    onPartnerSearchInApollo,
    resetApolloResult,
    apolloResult,
    partnerDuplicatesHeaderActions,
    partnerDuplicatesContentActions,
    partnerDuplicatesContentMessages,
    partnerDuplicatesHeaderMessages,
    update,
    isApolloResultVisible,
  }
}
